import type { ModelDeleteContentProps } from "./model-delete-content.type";

import { Button, Space, Typography, Divider } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useDelete } from "@/hooks/queries";
import { useAsyncActions } from "@/modules/async-actions";
import {
  getModelApiKey,
  isModelDeleteAsync,
  type ModelItem,
} from "@/modules/model";
import { useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";

const ModelDeleteContent = <Item extends ModelItem>({
  modelKey,
  item,
  nameParam = "name",
  onDeleted,
  ...mutationOptions
}: ModelDeleteContentProps<Item>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeSidePanel } = useSidePanel();

  const apiKey = getModelApiKey(modelKey);
  const isAsyncDelete = isModelDeleteAsync(modelKey);

  const { setDeletingState } = useAsyncActions();

  const { mutate, isPending, isError, reset } = useDelete(
    apiKey,
    item.id,
    mutationOptions
  );

  const onClose = () => {
    closeSidePanel();
  };

  const onDelete = () => {
    mutate(null, {
      onSuccess: () => {
        onClose();

        if (isAsyncDelete) {
          setDeletingState(modelKey, item.id);
        }

        if (typeof onDeleted === "function") {
          onDeleted();
        } else {
          // go back if we are on the current page
          const pathName = location.pathname;
          const lastSegment = pathName.substring(pathName.lastIndexOf("/") + 1);
          if (lastSegment === `${item.id}`) {
            navigate(-1);
          }
        }
      },
    });
  };

  return (
    <>
      <Alert display={isError} closable={{ afterClose: () => reset() }} />
      <Typography.Text>{`${item[nameParam]}`}</Typography.Text>
      {isAsyncDelete && (
        <>
          <Divider />
          <Alert
            display={true}
            title={t("global:asyncDeleteNotification")}
            type="warning"
            closable={false}
          />
        </>
      )}
      <Divider />
      <Space>
        <Button onClick={onClose}>{t("global:cancel")}</Button>
        <Button onClick={onDelete} loading={isPending} type="primary">
          {t("global:delete")}
        </Button>
      </Space>
    </>
  );
};

export default ModelDeleteContent;
